import React, { useEffect, useContext, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from 'react-router';
import { Link } from "react-router-dom";

import {
	Pager,
	Button
	// EmptyItem
} from "rhombus-react";

import { Creatives } from "reducers/Creatives";
import { hasActiveFilters } from "reducers/common/ColumnFilters";
import { hasSocialConnections } from "selectors/Social";

import {
	CREATIVE_ROW_COUNT_OPTIONS
	, SEARCH_DEBOUNCE
	, UPLOAD_CREATIVE_ID
	, UPLOAD_CREATIVE_FILE_TYPES
	, JCRM_SOCKET
} from "utils/constants";
import { useCollectionFiltering } from "utils/DataGrid";

import { setKey, removeKey } from 'utils/Keyboard';
import {
	setDownloadAS, DOWNLOAD_AS_KEY
	, setPartnerTags, PARTNER_TAGS_KEY
} from 'utils/Flags';

import Modals from "./Modals";
import Toolbar from "./Toolbar";
import Grid from "./Grid";

import ImportExportDropdown from "./ImportExportButton";
import { CreativeUploadContext } from "context/CreativeUploadContext";

import { useOnMountFTMessage } from "hooks/Notification";
import { CREATIVE_COLUMNS } from "widgets/grid/columns/Creatives";
import FilterTagList from "widgets/toolbars/FilterTagList";
import EmptySection from "widgets/EmptySection";
import Permissioned from "widgets/Permissioned";
import useCancelToken from "hooks/useCancelToken";

import './CreativeGrid.css';

const distinctColumns = CREATIVE_COLUMNS
	// filter out labelsCnt since we're hardcoding the value as it isn't supported in the distincts endpoint
	.filter(v => v.key !== "labelsCnt" && /checkbox|range/gi.test(v.filter))
	.map(v => v.key)
	;

export default function CreativeGrid(props){
	const dispatch = useDispatch()
		, { libraryId } = props;

	const {
		creatives,
		routeLocation,
		hasSocial,
	} = useSelector((state) => {
		return {
			routeLocation:state.router.location.pathname,
			creatives:state.creatives,
			hasSocial: hasSocialConnections(state),
		};
	});

	const { getNewToken } = useCancelToken(true);

	const libraryName = useSelector(state => state.libraryProperties.name);
	const { stageUpload, isValidating: isValidatingCreativeUpload, isUploading } = useContext(CreativeUploadContext);

	const { push } = useHistory();

	/**
	 * Set Up Keyboard Shortcut Feature Flags
	 * See utils/Flags for more info
	 */
	useEffect(() => {
		setKey(DOWNLOAD_AS_KEY, setDownloadAS);
		setKey(PARTNER_TAGS_KEY, setPartnerTags);
		return () => {
			removeKey(DOWNLOAD_AS_KEY, PARTNER_TAGS_KEY);
		};
	}, []);

	const uploadInputRef = useCallback((node) => {
		if(node === null){ return; }

		const { files } = node;
		if(!isUploading && files.length > 0) {
			node.value = '';
		}
	}, [isUploading]);

	let returnPath = `${props.match.url}/`
		, items = [
			<Modals key="modals"
				libraryId = {libraryId}
				returnPath = {returnPath}
			/>
			, <input
				key="file-upload"
				id={UPLOAD_CREATIVE_ID}
				type="file"
				multiple
				className="hidden"
				onChange={uploadFiles}
				accept={UPLOAD_CREATIVE_FILE_TYPES}
				ref={uploadInputRef}
			/>
		]
		, hasSocialPath = hasSocial ? "create-concept/social/" : "no-integration/"
		, { state } = useLocation()
		, { initialSearchTerm, initialFilters } = state || {};


	useEffect(() => {
		dispatch(Creatives.loadCreativeDistincts(libraryId, distinctColumns));

		/*
			As of now the distincts endpoint does not support labelsCnt so filtering doesn't work properly.
			For now we're hardcoding the value of labelsCnt and updating it here directly
		*/
		dispatch(Creatives.updateColumnFilter("labelsCnt", {
			options: [0, 50], // eslint-disable-line no-magic-numbers
		}));
	}, [libraryId]);

	// Connect to sockets for VAST uploads
	useOnMountFTMessage(({ detail }) => {
		let { message } = detail
			, { task, status, data } = message
			, shouldDispatch = false
			;

		if(message.origin !== JCRM_SOCKET){ return; }

		// We only want to update the grid from the Encode step for in progress, failure, and success
		if(/Encode vast video/gi.test(task)){
			if(/in progress|failed|success/gi.test(status)){
				shouldDispatch = true;
			}
		}

		if(/Encode vast audio/gi.test(task)){
			if(/in progress|failed|success/gi.test(status)){
				shouldDispatch = true;
			}
		}

		if(/upload video/gi.test(task)){
			// Uploading an asset exposes the same task name, only dispatch if a creative object is there
			if(/success/gi.test(status) && data.creative){
				shouldDispatch = true;
			}
		}

		if(/Import social creatives/gi.test(task) && /success/gi.test(status)){
			dispatch(Creatives.loadCreatives(libraryId));
		}

		if(/Generate assets and versions/gi.test(task) && /success/gi.test(status)){
			dispatch(Creatives.updateCreativeVersionCountsFromSocket(data.creatives));
		}

		if(shouldDispatch){
			dispatch(Creatives.addSocketCreative(data?.creativeLibrary, data?.creative, { task, status }));
		}
	});

	const {
		filterInfo: { page, rpp, searchTerm, sort },
		columnFilters,
	} = useCollectionFiltering(
		creatives,
		Creatives.setAdvancedFilter,
		() => {
			dispatch(Creatives.clearCreatives()).then(() => {
				dispatch(Creatives.loadCreatives(libraryId, getNewToken()));
			});
		},
		() => {
			if(initialSearchTerm){ dispatch(Creatives.setSearchTerm(initialSearchTerm));}
			if(initialFilters){
				initialFilters.forEach((col) => {
					dispatch(Creatives.updateColumnFilter(col.column, col));
				});
			}
		}
	);

	const pagerProps={
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: creatives.totalPages,
		maxItems: creatives.totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,
		disabled: !creatives.isLoaded,

		onPageChange: newPage => dispatch(Creatives.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(Creatives.setRPP(newRPP)),
	};

	let $toolbar = (
		<Toolbar key = "toolbar"
			searchTerm = { searchTerm }
			searchDebounce = {SEARCH_DEBOUNCE}
			libraryId = { libraryId }
			isLoading = { !creatives.isLoaded }
			handleSearch = { (newSearchTerm) => {
				dispatch(Creatives.setSearchTerm(newSearchTerm));
			}}
		/>
	);

	let $filterTagList = (
		<FilterTagList
			key="filter-tags"
			Reducer = {Creatives}
			filters = {columnFilters}
		/>
	);

	function uploadFiles(e){
		stageUpload(libraryId, libraryName, e.currentTarget.files);
	}

	if(creatives.isLoaded && creatives.items.length === 0) {
		function resetAllFilters(){
			dispatch(Creatives.clearColumnFilters());
			dispatch(Creatives.setSearchTerm(""));
		}

		items = items.concat([$toolbar, $filterTagList]);

		let emptyProps = null;

		if(hasActiveFilters(creatives)){
			emptyProps = {
				className: "empty-creatives no-filter-results",
				header: "No results to display",
				message: "Try adjusting your filters to find what you are looking for",
				src: "/images/noFiles.svg",
				buttons:[
					<Button key="clear" onClick={resetAllFilters}>Clear All Filters</Button>
				],
			};
		} else if(searchTerm){
			emptyProps = {
				className: "empty-creatives no-search-results",
				header: "No search results to display",
				message: "Try adjusting your search to find what you are looking for",
				src: "/images/noSearch.svg",
				buttons:[
					<Button key="clear" onClick={() => dispatch(Creatives.setSearchTerm(""))}>Clear Search</Button>
				],
			};
		} else if(creatives.hasErrorLoading) {
			emptyProps = {
				key: "error-item",
				header: "There was a Problem Loading Creatives",
				icon: "fas fa-bug",
				buttons: [
					<Button key="reload" icon="refresh"
						onClick={resetAllFilters}>
						Re-Try
					</Button>
				],
			};
		} else {
			// If user has permission to upload creative they can create a VAST
			let noCreativeButtons = [
				<Permissioned key="upload-creative" domain="Creative View" action="Upload Ad Files">
					<Button disabled={isValidatingCreativeUpload}><label htmlFor={UPLOAD_CREATIVE_ID}>Upload Creative</label></Button>
					<Button>
						<Link className="new-creative-button" to={`${routeLocation}modal/create-vast/`}>
							Create VAST Videos
						</Link>
					</Button>
					<Button>
						<Link className="new-creative-button" to={`${routeLocation}modal/create-audio/`}>
							Create Audio
						</Link>
					</Button>
					<Button key="create-native-ad">
						<Link className="new-creative-button" to={`${routeLocation}modal/create-native-ad/`}>
							Create Native Ad
						</Link>
					</Button>
				</Permissioned>,
				<Permissioned key="add-open-concept" action="Upload Ad Files" domain="Creative View">
					<Button>
						<Link className="new-creative-button" to={`${routeLocation}modal/create-concept/open/`}>
							Open Display Concept
						</Link>
					</Button>
				</Permissioned>,
				<Permissioned key="social-concept" domain="Creative View" action="Upload Ad Files">
					<Button onClick={()=>push(`${routeLocation}modal/${hasSocialPath}`)}>Social Concept</Button>
				</Permissioned>
				, <ImportExportDropdown key="import-export-creatives" theme="blue" icon={null} />
			];
			emptyProps = {
				className: "empty-creatives",
				header: "No creatives have been added",
				message: "Start by selecting from the following options",
				src: "/images/noFiles.svg",
				buttons: noCreativeButtons,
			};
		} //end if(compiledFilters)

		items = items.concat(<EmptySection key="empty" {...emptyProps} />);
	} else {
		items = items.concat([
			$toolbar
			, $filterTagList
			, <Grid
				key="grid"
				libraryId={ libraryId }
				creatives={ creatives.items }
				isLoading={ !creatives.isLoaded }
				sort={ sort }
			/>
			, <Pager key="pager" { ...pagerProps } />
		]);
	}	//end if(creatives.isLoaded)

	return items;
}

CreativeGrid.displayName = "CreativeGrid";
CreativeGrid.defaultProps = {
	columnFilters:{},
};
